import React from 'react';
import { RadioGroup } from '@ark-ui/react/radio-group';
import clsx from 'clsx';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export interface RadioOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface RadioGroupInputProps extends BaseInputProps<string> {
  options: RadioOption[];
  /** Orientace skupiny. */
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

/**
 * Atomická skupina přepínačů postavená na Ark UI `RadioGroup`.
 *
 * Skupina nemá jeden fokusovatelný cíl pro `label[htmlFor]` — jméno dostává
 * radiogroup přes `aria-labelledby` na popisek z `<FormField />`. Dříve
 * používané mapování `htmlFor` na skrytý input první volby způsobovalo, že
 * klik na popisek pole tiše vybral první možnost.
 */
export const RadioGroupInput: React.FC<RadioGroupInputProps> = ({
  name,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  labelId,
  describedBy,
  testAttributes,
  options,
  orientation = 'vertical',
  className,
}) => (
  <RadioGroup.Root
    ref={ref}
    name={name}
    value={value ?? null}
    disabled={disabled}
    readOnly={readOnly}
    invalid={hasError}
    orientation={orientation}
    onValueChange={(details) => onChange(details.value ?? '')}
    onBlur={onBlur}
    className={clsx(styles.radioRoot, className)}
    aria-labelledby={labelId}
    aria-describedby={describedBy}
    {...testAttributes}
  >
    {options.map((option) => (
      <RadioGroup.Item
        key={option.value}
        value={option.value}
        disabled={option.disabled}
        className={styles.radioItem}
        data-testid={`${testAttributes['data-testid']}-option-${option.value}`}
      >
        <RadioGroup.ItemControl
          className={styles.radioControl}
          data-invalid={hasError ? '' : undefined}
        />
        <RadioGroup.ItemText className={styles.radioItemText}>
          {option.label}
        </RadioGroup.ItemText>
        <RadioGroup.ItemHiddenInput />
      </RadioGroup.Item>
    ))}
  </RadioGroup.Root>
);

RadioGroupInput.displayName = 'RadioGroupInput';
